// Skills v2 — on-the-agent UX (2026-08-19).
//
// Renders the "Skills — what this agent can do" surface inside the Agent
// Overview tab. The mockup is the conformance spec:
//   docs/design/skills-framework-v2-mockup.html  <section id="s1">.
// Outcome-first, zero jargon: the tenant manages skills right here on the
// agent (add / preview / remove) instead of a separate admin tab.
//
// All formatting logic lives in ./skillFormat (pure, 100%-tested). This file
// is the renderer + the small amount of fetch/UI-state glue.

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { COLORS } from "../../../theme/colors";
import { MATERIALS } from "../../../theme/materials";
import { skillsApi, observabilityApi } from "../../../api/client";
import {
  splitSuggestions,
  healthOf,
  whyText,
  capabilityChips,
  formatPct,
  formatDelta,
  toneColor,
} from "./skillFormat";

// The suggestions endpoint returns a bare array; other app endpoints wrap in
// {success, data}. Tolerate both shapes.
function unwrapArray(res) {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.data)) return res.data;
  return [];
}
function unwrapObj(res) {
  if (res && res.data && typeof res.data === "object" && !Array.isArray(res.data)) {
    return res.data;
  }
  return res || null;
}

const CARD = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 16,
  background: COLORS.surface,
  boxShadow: MATERIALS.spatialShadow(),
  padding: "14px 15px",
};

const SECT_LABEL = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: COLORS.textDim,
  margin: "18px 0 10px",
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const ICON = {
  width: 38,
  height: 38,
  borderRadius: 11,
  display: "grid",
  placeItems: "center",
  fontSize: 19,
  flex: "none",
  background: COLORS.accentSoft,
  border: `1px solid ${COLORS.accent}`,
};

function Pill({ label, tone }) {
  const color = toneColor(tone, COLORS);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
      color, background: `${color}1F`, border: `1px solid ${color}66`,
    }}>{tone === "warn" ? "▲" : "●"} {label}</span>
  );
}

function btnStyle(variant) {
  if (variant === "primary") {
    return {
      font: "inherit", fontSize: 13, fontWeight: 700, color: "#fff", border: 0,
      cursor: "pointer", background: COLORS.accent, padding: "9px 15px",
      borderRadius: 9, display: "inline-flex", alignItems: "center", gap: 7,
    };
  }
  return {
    font: "inherit", fontSize: 12, fontWeight: 600, color: COLORS.textMuted,
    cursor: "pointer", background: COLORS.surface,
    border: `1px solid ${COLORS.border}`, padding: "7px 12px", borderRadius: 9,
    display: "inline-flex", alignItems: "center", gap: 7,
  };
}

// One "Uses:" chip. Satisfied → calm green (non-interactive). Unsatisfied →
// clickable "⚠ Needs X · Connect" that deep-links to the Wiring tab.
function CapabilityChip({ chip, onNavigateWiring }) {
  const color = toneColor(chip.tone, COLORS);
  const base = {
    display: "inline-flex", alignItems: "center", gap: 6, font: "inherit",
    fontSize: 11.5, fontWeight: 650, padding: "5px 10px", borderRadius: 999,
    border: `1px solid ${color}66`, color, background: `${color}1F`,
  };
  if (chip.deepLink) {
    return (
      <button
        type="button"
        style={{ ...base, cursor: "pointer" }}
        onClick={() => onNavigateWiring && onNavigateWiring(chip.deepLink)}
      >{chip.label}</button>
    );
  }
  return <span style={base}>{chip.label}</span>;
}

// Inline before/after panel for a suggested skill. Pick one of the tenant's
// golden-set datasets, then run the eval engine and render the mockup's delta
// + bars + example.
function PreviewImpactPanel({ agentId, skillName }) {
  const [datasets, setDatasets] = useState([]);
  const [datasetsReady, setDatasetsReady] = useState(false);
  const [datasetId, setDatasetId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await observabilityApi.listDatasets();
        if (!cancelled) setDatasets(unwrapArray(res));
      } catch {
        if (!cancelled) setDatasets([]);
      } finally {
        if (!cancelled) setDatasetsReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const run = async () => {
    if (!datasetId) {
      setError("Choose a dataset to preview against.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await skillsApi.previewImpact(agentId, skillName, datasetId);
      setResult(unwrapObj(res));
    } catch {
      setError("Couldn't run the preview just now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const example = result && Array.isArray(result.examples) ? result.examples[0] : null;

  return (
    <div style={{
      marginTop: 12, padding: "14px 15px", borderRadius: 14,
      border: `1px solid ${COLORS.border}`, background: COLORS.surfaceHover,
    }}>
      <div style={{ fontSize: 12.5, color: COLORS.textMuted, marginBottom: 8 }}>
        Before you commit, we run this agent on a sample of your own questions —
        once without the skill, once with it.
      </div>
      {datasetsReady && datasets.length === 0 ? (
        <div style={{ fontSize: 12.5, color: COLORS.textDim }}>
          No golden-set datasets yet — create one in Observability → Datasets.
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <select
            aria-label="Dataset"
            value={datasetId}
            onChange={(e) => setDatasetId(e.target.value)}
            style={{
              flex: 1, minWidth: 180, font: "inherit", fontSize: 13, color: COLORS.text,
              background: COLORS.surface, border: `1px solid ${COLORS.border}`,
              borderRadius: 9, padding: "8px 11px",
            }}
          >
            <option value="">Choose a dataset…</option>
            {datasets.map((ds) => (
              <option key={ds.id} value={ds.id}>{ds.name}</option>
            ))}
          </select>
          <button type="button" style={btnStyle("primary")} onClick={run} disabled={loading}>
            {loading ? "Running…" : "Run preview"}
          </button>
        </div>
      )}

      {error && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: toneColor("warn", COLORS) }}>{error}</div>
      )}

      {result && (
        <div style={{ marginTop: 14 }}>
          <div style={{
            display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap",
            border: `1px solid ${COLORS.border}`, borderRadius: 14,
            background: COLORS.surface, padding: "16px 18px",
          }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <span style={{ fontSize: 32, fontWeight: 800, color: toneColor("ok", COLORS) }}>
                {formatDelta(result.delta)}
              </span>
              <span style={{
                fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase",
                color: COLORS.textDim, fontWeight: 700,
              }}>accuracy lift</span>
            </div>
            <div style={{ flex: 1, minWidth: 200, display: "flex", flexDirection: "column", gap: 9 }}>
              <Bar label="Without" fraction={result.baseline_accuracy} tone="dim" />
              <Bar label="With skill" fraction={result.with_skill_accuracy} tone="ok" />
              <div style={{ fontSize: 12, color: COLORS.textDim }}>
                Sampled {result.scored ?? 0} of your questions
                {result.errored ? ` · ${result.errored} couldn't be scored` : ""} · powered by the eval engine
              </div>
            </div>
          </div>

          {example && (
            <div style={{
              marginTop: 12, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden",
            }}>
              <div style={{
                padding: "10px 13px", borderBottom: `1px solid ${COLORS.border}`,
                fontSize: 12.5, color: COLORS.text, background: COLORS.surfaceHover,
              }}>
                <b>Q:</b> {example.input}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                <div style={{ padding: "11px 13px", borderRight: `1px solid ${COLORS.border}`, fontSize: 12.5 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: COLORS.pink, marginBottom: 5 }}>
                    ✕ Without the skill
                  </div>
                  <div style={{ color: COLORS.text }}>{example.baseline_output}</div>
                </div>
                <div style={{ padding: "11px 13px", fontSize: 12.5 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: toneColor("ok", COLORS), marginBottom: 5 }}>
                    ✓ With the skill
                  </div>
                  <div style={{ color: COLORS.text }}>{example.with_skill_output}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Accessible on/off switch for the auto-attach opt-out.
function Toggle({ checked, disabled, onChange, label }) {
  const on = !!checked;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      style={{
        position: "relative", width: 40, height: 22, flex: "none",
        borderRadius: 999, border: `1px solid ${on ? COLORS.accent : COLORS.border}`,
        background: on ? COLORS.accent : COLORS.surfaceHover,
        cursor: disabled ? "default" : "pointer", padding: 0,
        opacity: disabled ? 0.6 : 1, transition: "background .15s, border-color .15s",
      }}
    >
      <span style={{
        position: "absolute", top: 2, left: on ? 20 : 2, width: 16, height: 16,
        borderRadius: "50%", background: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,.3)",
        transition: "left .15s",
      }} />
    </button>
  );
}

function Bar({ label, fraction, tone }) {
  const frac = typeof fraction === "number" && !Number.isNaN(fraction) ? fraction : 0;
  const fill = tone === "ok" ? toneColor("ok", COLORS) : COLORS.textDim;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12 }}>
      <span style={{ width: 70, color: COLORS.textDim }}>{label}</span>
      <span style={{ flex: 1, height: 9, borderRadius: 999, background: COLORS.border, overflow: "hidden" }}>
        <i style={{ display: "block", height: "100%", width: `${Math.round(frac * 100)}%`, background: fill }} />
      </span>
      <span style={{ width: 44, textAlign: "right", fontWeight: 700, color: tone === "ok" ? toneColor("ok", COLORS) : COLORS.text }}>
        {formatPct(fraction)}
      </span>
    </div>
  );
}

// Lightweight placeholder shown in the Active/Suggested lists while they load —
// the section's header + toggle are already visible, so this only fills the list.
function SkeletonRows() {
  const bar = {
    height: 44, borderRadius: 12, border: `1px solid ${COLORS.border}`,
    background: COLORS.surface, opacity: 0.55,
  };
  return (
    <div style={{ display: "grid", gap: 8 }} aria-busy="true" aria-label="Loading skills">
      <div style={bar} />
      <div style={bar} />
    </div>
  );
}

export default function SkillsSection({ agentId, canEdit, onNavigateWiring }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [active, setActive] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [capsBySkill, setCapsBySkill] = useState({});
  const [previewFor, setPreviewFor] = useState(null);
  const [addMsg, setAddMsg] = useState({});
  const [autoAttach, setAutoAttach] = useState(null); // null = unknown yet
  const [autoBusy, setAutoBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [allSkills, setAllSkills] = useState(null); // lazy-loaded tenant skills
  const [pickName, setPickName] = useState("");
  const [pickBusy, setPickBusy] = useState(false);
  const [pickMsg, setPickMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Auto-attach opt-out state — best-effort, never blocks the section.
      try {
        const st = await skillsApi.getAutoAttach(agentId);
        const obj = unwrapObj(st);
        setAutoAttach(obj ? obj.auto_attach_enabled !== false : true);
      } catch {
        setAutoAttach(true); // default ON if unreadable
      }
      const res = await skillsApi.suggestions(agentId);
      const { active: a, suggested: s } = splitSuggestions(unwrapArray(res));
      setActive(a);
      setSuggested(s);
      // Fetch each suggested skill's capabilities for the "Uses:" chips.
      // Resilient: one skill's failure never blocks the others.
      const caps = {};
      await Promise.all(
        s.map(async (item) => {
          try {
            const capRes = await skillsApi.capabilities(agentId, item.skill_name);
            caps[item.skill_name] = capabilityChips(unwrapObj(capRes));
          } catch {
            caps[item.skill_name] = [];
          }
        })
      );
      setCapsBySkill(caps);
    } catch {
      setError("Couldn't load skills for this agent.");
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = useCallback(async (item) => {
    // Data-trigger skills attach against their matched table; general
    // (semantic-trigger) skills need no table — the platform attaches them
    // via the attach-general endpoint. Tolerate failure gracefully.
    try {
      if (item.trigger === "data" && item.matched_table) {
        await skillsApi.attach(agentId, item.skill_name, item.matched_table);
      } else {
        await skillsApi.attachGeneral(agentId, item.skill_name);
      }
      setAddMsg((m) => ({ ...m, [item.skill_name]: "Added — it's now active on this agent." }));
      load();
    } catch {
      setAddMsg((m) => ({
        ...m,
        [item.skill_name]: "Couldn't add it just now. If it needs a connection, connect it first.",
      }));
    }
  }, [agentId, load]);

  const handleRemove = useCallback(async (item) => {
    // Active skills carry an instance_id from the suggestions payload.
    // Defensive guard: never call detach without one.
    if (!item.instance_id) return;
    try {
      await skillsApi.detach(agentId, item.instance_id);
      load();
    } catch {
      /* keep the card; a failed detach shouldn't crash the section */
    }
  }, [agentId, load]);

  const handleToggleAuto = useCallback(async () => {
    if (autoBusy || autoAttach === null) return;
    const next = !autoAttach;
    setAutoAttach(next); // optimistic
    setAutoBusy(true);
    try {
      await skillsApi.setAutoAttach(agentId, next);
      if (next) load(); // re-attaching may surface new active skills
    } catch {
      setAutoAttach(!next); // revert on failure
    } finally {
      setAutoBusy(false);
    }
  }, [agentId, autoAttach, autoBusy, load]);

  const openPicker = useCallback(async () => {
    setPickerOpen((o) => !o);
    setPickMsg(null);
    if (allSkills === null) {
      try {
        // The "Choose a skill" picker must offer the tenant's GALLERY catalog
        // (authored + global published skills you CAN add) — NOT yourSkills(),
        // which returns only skills already ATTACHED elsewhere. attach-general
        // adopts any gallery/base skill by name, so a gallery-sourced pick is
        // exactly right; sourcing from yourSkills() made never-attached gallery
        // skills un-addable and emptied the picker on agents that already had
        // the attached ones active.
        const res = await skillsApi.gallery();
        setAllSkills(unwrapArray(res));
      } catch {
        setAllSkills([]);
      }
    }
  }, [allSkills]);

  const handleManualAdd = useCallback(async () => {
    if (!pickName || pickBusy) return;
    setPickBusy(true);
    setPickMsg(null);
    try {
      await skillsApi.attachGeneral(agentId, pickName);
      setPickMsg(`Added "${pickName}" — it's now active on this agent.`);
      setPickName("");
      load();
    } catch {
      setPickMsg("Couldn't add it just now. If it needs a connection, connect it first.");
    } finally {
      setPickBusy(false);
    }
  }, [agentId, pickName, pickBusy, load]);

  // Render the shell (header + toggle) immediately; only the Active/Suggested
  // lists wait on the network, so the section never sits behind a blank
  // "Loading skills…" wall.
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div>
        <h2 style={{ fontSize: 16, margin: 0, color: COLORS.text }}>
          Skills — what this agent can do
        </h2>
        <p style={{ margin: "4px 0 0", color: COLORS.textDim, fontSize: 13, maxWidth: "66ch" }}>
          Skills are ready-made playbooks that make this agent better at a job.
          Manage them right here on the agent — add, preview, or remove in a tap.
        </p>
      </div>

      {error && (
        <div style={{
          marginTop: 12, fontSize: 12.5, color: toneColor("warn", COLORS),
          border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "8px 11px",
        }}>{error}</div>
      )}

      {/* Auto-attach opt-out toggle */}
      {canEdit && autoAttach !== null && (
        <div style={{
          display: "flex", alignItems: "center", gap: 12, marginTop: 14,
          padding: "12px 14px", borderRadius: 12,
          border: `1px solid ${COLORS.border}`, background: COLORS.surface,
        }}>
          <Toggle
            checked={autoAttach}
            disabled={autoBusy}
            onChange={handleToggleAuto}
            label="Automatically suggest and attach skills"
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 650, color: COLORS.text }}>
              Auto-attach skills
            </div>
            <div style={{ fontSize: 12, color: COLORS.textDim, marginTop: 2 }}>
              {autoAttach
                ? "On — the platform matches this agent's data to skills and attaches strong matches for you. You can still add or remove any skill by hand."
                : "Off — you're curating skills manually. Nothing already attached is removed; new matches won't attach on their own."}
            </div>
          </div>
        </div>
      )}

      {/* Active on this agent */}
      <div style={{ ...SECT_LABEL, display: "flex", alignItems: "center", gap: 8 }}>
        <span>Active on this agent</span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => navigate("/skills?tab=gallery")}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 0, font: "inherit", fontSize: 12, fontWeight: 650, textTransform: "none", letterSpacing: 0, color: COLORS.accentText || COLORS.accent }}
        >Browse the gallery →</button>
      </div>
      {loading ? (
        <SkeletonRows />
      ) : active.length === 0 ? (
        <div style={{ fontSize: 12.5, color: COLORS.textDim }}>No skills active yet.</div>
      ) : (
        <div style={{ display: "grid", gap: 11 }}>
          {active.map((item) => {
            const health = healthOf(item);
            return (
              <div key={item.skill_name} style={CARD}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <div style={ICON}>💡</div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: 14, margin: 0, color: COLORS.text,
                      display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                    }}>
                      {item.display_name || item.skill_name} <Pill label={health.label} tone={health.tone} />
                    </h3>
                    <div style={{ color: COLORS.textMuted, fontSize: 12.5, marginTop: 3 }}>
                      {item.when_to_use}
                    </div>
                  </div>
                </div>
                {canEdit && (
                  <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button
                      type="button"
                      style={btnStyle("ghost")}
                      title="Remove this skill"
                      onClick={() => handleRemove(item)}
                    >Remove</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Suggested for this agent */}
      <div style={SECT_LABEL}>Suggested for this agent</div>
      {loading ? (
        <SkeletonRows />
      ) : suggested.length === 0 ? (
        <div style={{ fontSize: 12.5, color: COLORS.textDim }}>No skills suggested yet.</div>
      ) : (
        <div style={{ display: "grid", gap: 11 }}>
          {suggested.map((item) => {
            const chips = capsBySkill[item.skill_name] || [];
            const msg = addMsg[item.skill_name];
            return (
              <div key={item.skill_name} style={CARD}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <div style={ICON}>🎫</div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: 14, margin: 0, color: COLORS.text }}>{item.display_name || item.skill_name}</h3>
                    <div style={{ color: COLORS.textMuted, fontSize: 12.5, marginTop: 3 }}>
                      {item.when_to_use}
                    </div>
                  </div>
                </div>

                {/* 💡 Why suggested */}
                <div style={{
                  display: "flex", gap: 8, alignItems: "flex-start", marginTop: 10,
                  fontSize: 12.5, color: COLORS.textMuted, background: COLORS.accentSoft,
                  border: `1px solid ${COLORS.accent}`, borderRadius: 10, padding: "8px 11px",
                }}>
                  <span>💡</span>
                  <div><b style={{ color: COLORS.text }}>Why suggested:</b> {whyText(item)}</div>
                </div>

                {/* Uses: chip row */}
                {chips.length > 0 && (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                    marginTop: 10, fontSize: 12, color: COLORS.textDim,
                  }}>
                    Uses:
                    {chips.map((chip, i) => (
                      <CapabilityChip key={i} chip={chip} onNavigateWiring={onNavigateWiring} />
                    ))}
                  </div>
                )}

                {canEdit && (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
                    <button type="button" style={btnStyle("primary")} onClick={() => handleAdd(item)}>
                      ＋ Add skill
                    </button>
                    <button
                      type="button"
                      style={btnStyle("ghost")}
                      onClick={() => setPreviewFor((p) => (p === item.skill_name ? null : item.skill_name))}
                    >Preview impact</button>
                  </div>
                )}

                {msg && (
                  <div style={{ marginTop: 8, fontSize: 12, color: COLORS.textDim }}>{msg}</div>
                )}

                {previewFor === item.skill_name && (
                  <PreviewImpactPanel agentId={agentId} skillName={item.skill_name} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add a skill manually (any skill in your library, matched or not) */}
      {canEdit && (
        <div style={{ marginTop: 18 }}>
          <button type="button" style={btnStyle("ghost")} onClick={openPicker}>
            {pickerOpen ? "Close" : "＋ Add a skill manually"}
          </button>
          {pickerOpen && (
            <div style={{ ...CARD, marginTop: 10 }}>
              <div style={{ fontSize: 12.5, color: COLORS.textMuted, marginBottom: 9 }}>
                Attach any skill from your library to this agent — even one the
                platform didn't suggest. Wrote your own?{" "}
                <span style={{ color: COLORS.textDim }}>Wrote your own?{" "}
                  <button type="button" onClick={() => navigate("/skills?tab=author")}
                    style={{ background: "none", border: "none", padding: 0, font: "inherit", cursor: "pointer", color: COLORS.accentText || COLORS.accent, fontWeight: 650 }}>Author it in the Skills tab →</button>{" "}then pick it here.</span>
              </div>
              {allSkills === null ? (
                <div style={{ fontSize: 12.5, color: COLORS.textDim }}>Loading your skills…</div>
              ) : (() => {
                const activeNames = new Set(active.map((a) => a.skill_name));
                const options = allSkills.filter(
                  (s) => !activeNames.has(s.name || s.skill_name),
                );
                if (options.length === 0) {
                  return (
                    <div style={{ fontSize: 12.5, color: COLORS.textDim }}>
                      No more skills to add.{" "}
                      <button type="button" onClick={() => navigate("/skills?tab=author")}
                        style={{ background: "none", border: "none", padding: 0, font: "inherit", cursor: "pointer", color: COLORS.accentText || COLORS.accent, fontWeight: 650 }}>Author one →</button>
                    </div>
                  );
                }
                return (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <select
                      aria-label="Skill to attach"
                      value={pickName}
                      onChange={(e) => setPickName(e.target.value)}
                      style={{
                        flex: 1, minWidth: 200, font: "inherit", fontSize: 13, color: COLORS.text,
                        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                        borderRadius: 9, padding: "8px 11px",
                      }}
                    >
                      <option value="">Choose a skill…</option>
                      {options.map((s) => {
                        const nm = s.name || s.skill_name;
                        return <option key={nm} value={nm}>{s.display_name || s.title || nm}</option>;
                      })}
                    </select>
                    <button
                      type="button"
                      style={btnStyle("primary")}
                      onClick={handleManualAdd}
                      disabled={!pickName || pickBusy}
                    >{pickBusy ? "Adding…" : "＋ Add skill"}</button>
                  </div>
                );
              })()}
              {pickMsg && (
                <div style={{ marginTop: 9, fontSize: 12, color: COLORS.textDim }}>{pickMsg}</div>
              )}
            </div>
          )}
        </div>
      )}

      <div style={{
        display: "flex", gap: 10, alignItems: "flex-start", fontSize: 12.5,
        color: COLORS.textMuted, background: COLORS.accentSoft,
        border: `1px solid ${COLORS.accent}`, borderRadius: 12, padding: "11px 14px", marginTop: 16,
      }}>
        <span>🧭</span>
        <div>
          <b style={{ color: COLORS.text }}>One flow:</b> everything about this
          agent's skills lives here — auto-attach on or off, add by hand, preview, or remove without leaving the agent.
        </div>
      </div>
    </div>
  );
}
